import i18next from 'i18next';
import { every, includes, map } from 'lodash';
import * as Yup from 'yup';

import {
  AGENTS_VALIDATION_SCHEMA,
  INTERVAL_COMMISSION_VALIDATION_SCHEMA,
} from 'components';
import { P2PProviderType, PayoutProtectionStatus } from 'enums';
import {
  P2PProviderFormValues,
  PayoutResponseErrorIndicator,
  TradeMethod,
} from 'types';
import { validationUtils } from 'utils';

import { P2P_PROVIDER_TYPES_WITHOUT_TRADE_METHODS } from './constants';

export const protectionStatusColor: Record<
  PayoutProtectionStatus,
  'success' | 'warning' | 'error'
> = {
  [PayoutProtectionStatus.FullyConfigured]: 'success',
  [PayoutProtectionStatus.Legacy]: 'warning',
  [PayoutProtectionStatus.Unsafe]: 'error',
};

export const parseCommaSeparatedList = (
  value: string | string[] | undefined,
): string[] =>
  typeof value === 'string'
    ? value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
    : value || [];

// Parses the JSON-text errorIndicators field into a typed array. Returns [] for empty/invalid
// input — validation is handled separately by the Yup test so a bad blob never reaches submit.
export const parseErrorIndicators = (
  value: string | undefined,
): PayoutResponseErrorIndicator[] => {
  if (!value || !value.trim()) {
    return [];
  }
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed)
      ? (parsed as PayoutResponseErrorIndicator[])
      : [];
  } catch {
    return [];
  }
};

const isValidErrorIndicatorsJson = (value: string | undefined): boolean => {
  if (!value || !value.trim()) {
    return true;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return false;
  }
  if (!Array.isArray(parsed) || parsed.length === 0) {
    return false;
  }
  return parsed.every((item) => {
    if (typeof item !== 'object' || item === null) {
      return false;
    }
    const indicator = item as Record<string, unknown>;
    if (
      typeof indicator.fieldPath !== 'string' ||
      !indicator.fieldPath.trim()
    ) {
      return false;
    }
    if (
      indicator.equals !== undefined &&
      (!Array.isArray(indicator.equals) || indicator.equals.length === 0)
    ) {
      return false;
    }
    if (
      indicator.presentAndTruthy !== undefined &&
      typeof indicator.presentAndTruthy !== 'boolean'
    ) {
      return false;
    }
    return (
      indicator.equals !== undefined || indicator.presentAndTruthy === true
    );
  });
};

const testUniquePaymentType = (
  selectedTradeMethods: { id: string }[] | undefined,
  allTradeMethods: TradeMethod[],
) => {
  const ids = map(selectedTradeMethods, 'id');
  if (!ids.length) {
    return true;
  }
  let paymentTypeId: string;
  return every(allTradeMethods, (tm) => {
    if (!includes(ids, tm.id)) {
      return true;
    }
    if (!paymentTypeId) {
      paymentTypeId = tm.paymentTypeId;
    }
    return tm.paymentTypeId === paymentTypeId;
  });
};

// Shape shared by payin and payout config: url/trade-methods requirement gating, common toggles,
// fees, and amount range. Payin/payout each add their own fields on top of this.
const getBaseConfigValidationSchemaShape = (
  allTradeMethods: TradeMethod[],
  providerType: P2PProviderType,
  zeroFeesForbidden: boolean,
) => {
  const noUrlAndTradeMethodsRequired =
    P2P_PROVIDER_TYPES_WITHOUT_TRADE_METHODS.includes(providerType);

  const { t } = i18next;
  return {
    url: Yup.string().when('enabled', {
      is: (enabled: boolean) => !!enabled && !noUrlAndTradeMethodsRequired,
      then: (schema) => schema.required(t('errors.required')),
      otherwise: (schema) => schema.nullable().optional(),
    }),
    configOverrides: Yup.object(),
    credentials: Yup.object(),
    enabled: Yup.boolean(),
    pollingEnabled: Yup.boolean(),
    useOwnPaymentType: Yup.boolean(),
    responseTimeout: Yup.number().nullable(),
    dailyCustomerCompletedOrdersLimit: Yup.number().min(0).integer(),
    expectedFee: Yup.number().when('enabled', {
      is: (enabled: boolean) => !!enabled,
      then: (schema) =>
        schema
          .required(t('errors.required'))
          .test(
            'zero-fee-forbidden',
            t('errors.zero_fee_forbidden'),
            (value) => !zeroFeesForbidden || !!value,
          ),
      otherwise: (schema) => schema.nullable().optional(),
    }),
    tradeMethods: noUrlAndTradeMethodsRequired
      ? Yup.array()
          .of(
            Yup.object().shape({
              id: Yup.string().required(t('errors.required')),
            }),
          )
          .optional()
      : Yup.array()
          .of(
            Yup.object().shape({
              id: Yup.string().required(t('errors.required')),
            }),
          )
          .when('useOwnPaymentType', {
            is: true,
            then: (schema) =>
              schema.test(
                'single-payment-type',
                t('errors.shop_restrictions.use_own_payment_type_error'),
                (value) => testUniquePaymentType(value, allTradeMethods),
              ),
          })
          .required(),
    ...validationUtils.getNaturalNumberRangeValidationSchema(
      'orderMinAmount',
      'orderMaxAmount',
    ),
  };
};

const getPayinConfigValidationSchema = (
  allTradeMethods: TradeMethod[],
  providerType: P2PProviderType,
  zeroFeesForbidden: boolean,
) => {
  const { t } = i18next;
  return Yup.object().shape({
    ...getBaseConfigValidationSchemaShape(
      allTradeMethods,
      providerType,
      zeroFeesForbidden,
    ),
    cancelEnabled: Yup.boolean().optional().nullable(),
    failOnMissedMapping: Yup.boolean().optional().nullable(),
    allowCustomerBank: Yup.boolean(),
    allowProviderAmountRandomization: Yup.boolean(),
    useProviderPaymentUrl: Yup.boolean(),
    keepNspkPhoneRequisites: Yup.boolean(),
    intervalCommissions: INTERVAL_COMMISSION_VALIDATION_SCHEMA,
    activeDisputeCount: Yup.number().when('enabled', {
      is: (enabled: boolean) => !!enabled,
      then: (schema) => schema.min(0).integer().required(t('errors.required')),
      otherwise: (schema) => schema.nullable().optional(),
    }),
  });
};

const hasAnyTemplateField = (parent: Record<string, unknown>): boolean => {
  const {
    statusFieldPath,
    acceptedStatuses,
    rejectedStatuses,
    requiredResponseFields,
    rejectedHttpStatusCodes,
    errorIndicators,
    acceptanceIndicators,
  } = parent ?? {};
  return (
    !!statusFieldPath ||
    parseCommaSeparatedList(acceptedStatuses as string).length > 0 ||
    parseCommaSeparatedList(rejectedStatuses as string).length > 0 ||
    parseCommaSeparatedList(requiredResponseFields as string).length > 0 ||
    parseCommaSeparatedList(rejectedHttpStatusCodes as string).length > 0 ||
    parseErrorIndicators(errorIndicators as string).length > 0 ||
    parseErrorIndicators(acceptanceIndicators as string).length > 0
  );
};

// Field-mode: acceptanceIndicators alone is a complete config (the create response has no
// status), so the status-mode fields are not required when acceptanceIndicators are present.
const hasAcceptanceIndicators = (parent: Record<string, unknown>): boolean =>
  parseErrorIndicators(parent?.acceptanceIndicators as string).length > 0;

const getPayoutConfigValidationSchema = (
  allTradeMethods: TradeMethod[],
  providerType: P2PProviderType,
) => {
  const { t } = i18next;
  return Yup.object().shape({
    // zero fees forbidden is a payin-only restriction — payout expectedFee is never checked for it
    ...getBaseConfigValidationSchemaShape(allTradeMethods, providerType, false),
    orderTimeout: Yup.number()
      .min(0, t('errors.natural_or_zero_number'))
      .integer(t('errors.natural_number'))
      .when('enabled', {
        is: (enabled: boolean) => !!enabled,
        then: (schema) =>
          schema
            .min(1, t('errors.natural_number'))
            .required(t('errors.required')),
        otherwise: (schema) => schema.nullable().optional(),
      }),
    payoutResponseTemplates: Yup.object().shape({
      statusFieldPath: Yup.string()
        .default('')
        .test(
          'required-when-any-template-field-set',
          t('errors.required'),
          function (value) {
            return (
              !hasAnyTemplateField(this.parent) ||
              hasAcceptanceIndicators(this.parent) ||
              !!value
            );
          },
        ),
      acceptedStatuses: Yup.string()
        .default('')
        .test(
          'required-when-any-template-field-set',
          t('errors.required'),
          function (value) {
            return (
              !hasAnyTemplateField(this.parent) ||
              hasAcceptanceIndicators(this.parent) ||
              parseCommaSeparatedList(value).length > 0
            );
          },
        ),
      rejectedStatuses: Yup.string()
        .default('')
        .test(
          'required-when-any-template-field-set',
          t('errors.required'),
          function (value) {
            return (
              !hasAnyTemplateField(this.parent) ||
              hasAcceptanceIndicators(this.parent) ||
              parseCommaSeparatedList(value).length > 0
            );
          },
        )
        .test(
          'no-overlap-with-accepted',
          t('errors.statuses_overlap'),
          function (rejectedValue) {
            const acceptedValue = this.parent?.acceptedStatuses;
            if (!acceptedValue || !rejectedValue) {
              return true;
            }
            const accepted = parseCommaSeparatedList(acceptedValue);
            const rejected = parseCommaSeparatedList(rejectedValue);
            const acceptedSet = new Set(accepted.map((s) => s.toLowerCase()));
            return !rejected.some((s) => acceptedSet.has(s.toLowerCase()));
          },
        ),
      requiredResponseFields: Yup.string().default(''),
      rejectedHttpStatusCodes: Yup.string()
        .default('')
        .test('valid-http-codes', t('errors.invalid_http_codes'), (value) => {
          if (!value) {
            return true;
          }
          const codes = parseCommaSeparatedList(value).map(Number);
          return codes.every(
            (code) => !isNaN(code) && code >= 100 && code <= 599,
          );
        }),
      errorIndicators: Yup.string()
        .default('')
        .test(
          'valid-error-indicators',
          t('errors.invalid_error_indicators'),
          (value) => isValidErrorIndicatorsJson(value),
        ),
      acceptanceIndicators: Yup.string()
        .default('')
        .test(
          'valid-acceptance-indicators',
          t('errors.invalid_acceptance_indicators'),
          (value) => isValidErrorIndicatorsJson(value),
        )
        .test(
          'exclusive-with-status-field-path',
          t('errors.acceptance_indicators_exclusive'),
          function (value) {
            return (
              parseErrorIndicators(value).length === 0 ||
              !this.parent?.statusFieldPath?.trim()
            );
          },
        ),
    }),
  });
};

export const getP2PProviderValidationSchema = (
  defaultAssetCurrencyId?: string,
  allTradeMethods: TradeMethod[] = [],
  validTypeCodes?: string[],
  zeroFeesForbidden = false,
): Yup.ObjectSchema<P2PProviderFormValues> => {
  const { t } = i18next;
  return Yup.object().shape({
    name: Yup.string().required(t('errors.required')),
    type: validTypeCodes?.length
      ? Yup.string().required(t('errors.required')).oneOf(validTypeCodes)
      : Yup.string().required(t('errors.required')),
    withdrawalFee: Yup.number().required(t('errors.required')),
    maxBalance: Yup.number().optional().nullable(),
    assetCurrencyId: Yup.string().required(t('errors.required')),
    fiatCurrencyId: Yup.string().required(t('errors.required')),
    currencyExchangeId: Yup.string().when('assetCurrencyId', {
      is: (assetCurrencyId: string) =>
        !assetCurrencyId || assetCurrencyId === defaultAssetCurrencyId,
      then: (schema) => schema.nullable().optional(),
      otherwise: (schema) => schema.required(t('errors.required')),
    }),
    assetCurrencyExchangeId: Yup.string().when('assetCurrencyId', {
      is: (assetCurrencyId: string) =>
        !assetCurrencyId || assetCurrencyId === defaultAssetCurrencyId,
      then: (schema) => schema.nullable().optional(),
      otherwise: (schema) => schema.required(t('errors.required')),
    }),
    payinConfig: Yup.object().when('type', ([type]) =>
      getPayinConfigValidationSchema(allTradeMethods, type, zeroFeesForbidden),
    ) as any,
    payoutConfig: Yup.object().when('type', ([type]) =>
      getPayoutConfigValidationSchema(allTradeMethods, type),
    ) as any,
    ...AGENTS_VALIDATION_SCHEMA,
    payoutLimits: Yup.object().when('payoutConfig.enabled', {
      is: (enabled: boolean) => !!enabled,
      then: (schema) =>
        schema.shape({
          limitCount: Yup.number()
            .min(0)
            .integer()
            .required(t('errors.required')),
          limitSum: Yup.number().min(0).required(t('errors.required')),
        }),
      otherwise: (schema) =>
        schema.shape({
          limitCount: Yup.number().min(0).integer(),
          limitSum: Yup.number().min(0),
        }),
    }),
    comment: Yup.string().optional().nullable(),
  }) as Yup.ObjectSchema<P2PProviderFormValues>;
};
